import React, { useState, useEffect, SyntheticEvent, useCallback } from 'react';
import { Button, CenteredModal, Input } from 'components';
import { Trans } from 'react-i18next';
import { createUseStyles, useTheme } from 'react-jss';

import { useToast, Loader } from 'components';
import illustration from '@app/illu/Image/EmptySessions.svg';
import { Theme } from '@app/theme';
import {
  useLiveSessions,
  useIsAdmin,
  useMoveParticipationToOtherSession,
} from '@app/api';
import { SessionItem } from '../../../components/SessionItem';

interface Props {
  participationId: string;
  isOpen: boolean;
  onRequestClose: () => any;
  onSuccess: () => any;
  organizationId?: string;
}

const useStyles = createUseStyles((theme: Theme) => ({
  sessionItem: {
    marginTop: theme.marginBase * 2,
  },
  emptyContainer: {
    backgroundColor: theme.colors.grayBackground,
    padding: theme.marginBase * 4,
    textAlign: 'center',
  },
  emptyTitle: {
    ...theme.fonts.h5,
    textAlign: 'center',
  },
  illustration: {
    width: '100%',
    maxWidth: 380,
    marginBottom: theme.marginBase * 3,
  },
}));

export const MoveToOtherSessionModal = ({
  participationId,
  isOpen,
  onRequestClose,
  onSuccess,
  organizationId,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const toast = useToast();
  const { mutateAsync: moveTo, isLoading } =
    useMoveParticipationToOtherSession(participationId);

  const [selected, setSelected] = useState<string | undefined>(undefined);

  const isAdmin = useIsAdmin();
  const { data: sessions = [], isLoading: fetching } =
    useLiveSessions(organizationId);

  useEffect(() => {
    setSelected(undefined);
  }, [setSelected, isOpen]);

  const onSend = useCallback(async () => {
    try {
      if (!selected) {
        return;
      }
      moveTo({ sessionId: selected });
      toast.saved();
      onRequestClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (e) {
      toast.exception(e);
    }
  }, [selected, moveTo, toast, onRequestClose, onSuccess]);

  if (!isAdmin) {
    return null;
  }

  return (
    <CenteredModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      title="Déplacer vers une autre session"
      id="invite_participant"
      buttons={[
        <Button
          onClick={onSend}
          color="green"
          small
          key="invite"
          disabled={!selected}
          text="Déplacer"
          isLoading={isLoading}
        />,
      ]}
    >
      {fetching && !sessions.length && <Loader />}
      {!fetching && !sessions.length && (
        <div className={classes.emptyContainer}>
          <img
            alt="illustrations"
            src={illustration}
            className={classes.illustration}
          />
          <h4 className={classes.emptyTitle}>
            <Trans i18nKey="participants.list.invite.empty" />
          </h4>
        </div>
      )}
      <Input
        value={selected}
        onChange={(e: SyntheticEvent<HTMLInputElement>) =>
          setSelected(e.currentTarget.value)
        }
        label="Session id"
      />
      {sessions.map((session) => (
        <SessionItem
          session={session}
          key={session.id}
          className={classes.sessionItem}
          small
          overWhite
          noStatus
          selected={selected === session.id}
          onClick={() => setSelected(session.id)}
        />
      ))}
    </CenteredModal>
  );
};
